import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { fetchPaymentCalendarLoad, schedulePaymentDate } from '../api/consignorPayments';
import type { CalendarDayLoad, PaymentLineItem, PaymentRow } from '../types/consignorPayments';
import { PAYMENT_CHANNEL_LABELS } from '../types/consignorPayments';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const DAY_LABELS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const ITEMS_PREVIEW = 3;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const moneyMx = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

function compactMxn(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `$${Math.round(n)}`;
}

function toDateStr(year: number, month0: number, day: number): string {
  return `${year}-${String(month0 + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function todayDateStr(): string {
  const t = new Date();
  return toDateStr(t.getFullYear(), t.getMonth(), t.getDate());
}

// ─── Item row ─────────────────────────────────────────────────────────────────

function ItemRow({ item }: { item: PaymentLineItem }): React.ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: '1px solid rgba(226,232,240,1)',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'rgba(248,250,252,1)',
          border: '1px solid rgba(226,232,240,1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.imageUrl ? (
          <Box
            component="img"
            src={item.imageUrl}
            alt={item.productName}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
          />
        ) : (
          <ImageIcon sx={{ color: 'action.disabled', fontSize: 20 }} />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {item.productName || 'Sin título'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {item.variantName || '—'}
          {item.artId ? ` · UU${item.artId}` : ''}
        </Typography>
      </Box>

      {/* Sale amount */}
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10 }}>
          Venta
        </Typography>
        <Typography variant="body2" fontWeight={700}>
          {moneyMx(item.soldAmount)}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Calendar ─────────────────────────────────────────────────────────────────

interface CalendarProps {
  year: number;
  month0: number; // 0-indexed
  selected: string | null;
  loadMap: Record<string, number>;
  loadingLoad: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDay: (dateStr: string) => void;
}

function PaymentCalendar({
  year,
  month0,
  selected,
  loadMap,
  loadingLoad,
  onPrevMonth,
  onNextMonth,
  onSelectDay,
}: CalendarProps): React.ReactElement {
  const today = todayDateStr();

  const cells = useMemo<(number | null)[]>(() => {
    const firstDay = new Date(year, month0, 1).getDay(); // 0 = Sunday
    const daysInMonth = new Date(year, month0 + 1, 0).getDate();
    const arr: (number | null)[] = [];
    for (let i = 0; i < firstDay; i += 1) arr.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) arr.push(d);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month0]);

  return (
    <Card elevation={0} sx={{ border: '1px solid rgba(226,232,240,1)', borderRadius: 2, p: 2 }}>
      {/* Month navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton size="small" onClick={onPrevMonth} sx={{ color: 'text.secondary' }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, textAlign: 'center' }}>
          {MONTH_NAMES[month0]} {year}
        </Typography>
        <IconButton size="small" onClick={onNextMonth} sx={{ color: 'text.secondary' }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Day-of-week headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
        {DAY_LABELS.map((label) => (
          <Typography
            key={label + String(DAY_LABELS.indexOf(label))}
            variant="caption"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: 'text.disabled',
              fontSize: 11,
              pb: 0.5,
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25 }}>
        {cells.map((day, idx) => {
          if (day == null) {
            return <Box key={`empty-${String(idx)}`} />;
          }

          const dateStr = toDateStr(year, month0, day);
          const isSelected = selected === dateStr;
          const isToday = dateStr === today;
          const load = loadMap[dateStr];

          return (
            <Box
              key={dateStr}
              onClick={() => onSelectDay(dateStr)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 52,
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: isSelected ? '#111827' : 'transparent',
                border: isToday && !isSelected ? '1.5px solid rgba(99,102,241,0.5)' : 'none',
                transition: 'background 0.12s',
                '&:hover': {
                  bgcolor: isSelected ? '#374151' : 'rgba(0,0,0,0.06)',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  fontWeight: isSelected || isToday ? 800 : 500,
                  color: (() => {
                    if (isSelected) return 'white';
                    if (isToday) return '#4F46E5';
                    return '#111827';
                  })(),
                  lineHeight: 1.2,
                }}
              >
                {day}
              </Typography>
              {load != null && !loadingLoad && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 8,
                    fontWeight: 700,
                    lineHeight: 1,
                    mt: 0.25,
                    color: isSelected ? 'rgba(255,255,255,0.65)' : '#B45309',
                  }}
                >
                  {compactMxn(load)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {loadingLoad && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
          <CircularProgress size={14} sx={{ color: 'text.disabled' }} />
        </Box>
      )}
    </Card>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export interface AdminSchedulePaymentModalProps {
  open: boolean;
  payment: PaymentRow | null;
  onClose: () => void;
  onScheduled: () => void;
}

export default function AdminSchedulePaymentModal({
  open,
  payment,
  onClose,
  onScheduled,
}: AdminSchedulePaymentModalProps): React.ReactElement {
  const nowYear = new Date().getFullYear();
  const nowMonth = new Date().getMonth();

  const [viewYear, setViewYear] = useState(nowYear);
  const [viewMonth0, setViewMonth0] = useState(nowMonth);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [calendarLoad, setCalendarLoad] = useState<CalendarDayLoad[]>([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      const now = new Date();
      setViewYear(now.getFullYear());
      setViewMonth0(now.getMonth());
      // Pre-select existing scheduledDate if already set
      setSelectedDate(payment?.scheduledDate ? payment.scheduledDate.slice(0, 10) : null);
      setShowAllItems(false);
      setSaveError(null);
    }
  }, [open, payment]);

  // Fetch calendar load when month view changes
  const loadCalendar = useCallback(async (year: number, month1: number) => {
    setLoadingCalendar(true);
    const result = await fetchPaymentCalendarLoad(year, month1);
    setLoadingCalendar(false);
    if (result.ok) setCalendarLoad(result.data);
  }, []);

  useEffect(() => {
    if (open) {
      loadCalendar(viewYear, viewMonth0 + 1).catch(() => {});
    }
  }, [open, viewYear, viewMonth0, loadCalendar]);

  const loadMap = useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    calendarLoad.forEach(({ date, amount }) => {
      m[date] = amount;
    });
    return m;
  }, [calendarLoad]);

  const handlePrevMonth = useCallback(() => {
    if (viewMonth0 === 0) {
      setViewMonth0(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth0((m) => m - 1);
    }
  }, [viewMonth0]);

  const handleNextMonth = useCallback(() => {
    if (viewMonth0 === 11) {
      setViewMonth0(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth0((m) => m + 1);
    }
  }, [viewMonth0]);

  const handleConfirm = useCallback(async () => {
    if (!payment || !selectedDate) return;
    setSaving(true);
    setSaveError(null);
    const result = await schedulePaymentDate(payment.id, selectedDate);
    setSaving(false);
    if (!result.ok) {
      setSaveError(result.error);
      return;
    }
    onScheduled();
  }, [payment, selectedDate, onScheduled]);

  const items: PaymentLineItem[] = payment?.items ?? [];
  const displayedItems = showAllItems ? items : items.slice(0, ITEMS_PREVIEW);
  const hasMore = items.length > ITEMS_PREVIEW;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Header */}
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: '1px solid rgba(226,232,240,1)',
          py: 2.5,
          px: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
            Programar fecha de pago
          </Typography>
          {payment && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Consignador: {payment.consignorName} · Pago #{payment.id}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: -0.25, color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            mt: 0.5,
          }}
        >
          {/* Left: Calendar */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Selecciona la fecha de pago
            </Typography>
            <PaymentCalendar
              year={viewYear}
              month0={viewMonth0}
              selected={selectedDate}
              loadMap={loadMap}
              loadingLoad={loadingCalendar}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectDay={setSelectedDate}
            />
            {selectedDate && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1.5, color: '#15803D', fontWeight: 600 }}
              >
                Fecha seleccionada:{' '}
                {new Intl.DateTimeFormat('es-MX', { dateStyle: 'long' }).format(
                  new Date(`${selectedDate}T12:00:00`)
                )}
              </Typography>
            )}
          </Box>

          {/* Right: Summary */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Resumen del pago
            </Typography>

            {/* Total card */}
            {payment && (
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
                  color: 'white',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    opacity: 0.65,
                    fontWeight: 700,
                  }}
                >
                  Monto total del pago
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, mb: 2 }}>
                  {moneyMx(payment.amount)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      Canal de pago
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {PAYMENT_CHANNEL_LABELS[payment.channel]}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      Artículos
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {items.length}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            )}

            {/* Items list */}
            <Card
              elevation={0}
              sx={{
                border: '1px solid rgba(226,232,240,1)',
                borderRadius: 2,
                overflow: 'hidden',
                maxHeight: 300,
                overflowY: 'auto',
              }}
            >
              {displayedItems.length > 0 &&
                displayedItems.map((item) => <ItemRow key={item.id} item={item} />)}
              {items.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.disabled">
                    Sin artículos
                  </Typography>
                </Box>
              )}
            </Card>

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
                <Button
                  size="small"
                  onClick={() => setShowAllItems((v) => !v)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#1D4ED8',
                    p: 0.5,
                  }}
                >
                  {showAllItems ? 'Ver menos' : `Ver todos los artículos (${items.length})`}
                </Button>
              </Box>
            )}

            {saveError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {saveError}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ borderTop: '1px solid rgba(226,232,240,1)', px: 3, py: 2, gap: 1.5 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.04)',
            px: 3,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={!selectedDate || saving}
          onClick={() => {
            handleConfirm().catch(() => {});
          }}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: '#111827',
            px: 3,
            '&:hover': { bgcolor: '#374151' },
          }}
        >
          {saving ? 'Guardando…' : 'Confirmar fecha'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
